import { queryOptions, useQuery } from '@tanstack/react-query';
import {
  fetchAdminArchivedCompanyDocuments,
  fetchAdminCompanyDocuments,
} from '@/api/(admin)/documents';
import type { AdminCompanyDocument } from '@/api/(admin)/documents/types';
import type { DocumentsQueryInput } from '../-types';
import { buildDocumentsQs, toDocumentsParams } from '../-utils/listParams';

/** Prefix for every admin-documents cache entry — what a restore invalidates. */
export const adminDocumentsQueryKey = ['admin', 'company-documents'] as const;

interface AdminDocumentsPage {
  rows: AdminCompanyDocument[];
  totalCount: number;
}

/**
 * Shared by the route loaders and the page, keyed on the *converted* params so
 * the prefetch warms exactly what the component reads.
 *
 * `isArchived` is part of the key rather than the querystring: the archive is a
 * separate route, so the two lists are separate cache entries that happen to
 * take the same parameters and answer the same envelope.
 */
export function adminDocumentsQueryOptions(input: DocumentsQueryInput) {
  const params = toDocumentsParams(input);
  const qs = buildDocumentsQs(params);
  return queryOptions({
    queryKey: [...adminDocumentsQueryKey, 'list', params] as const,
    queryFn: async (): Promise<AdminDocumentsPage> => {
      const res = params.isArchived
        ? await fetchAdminArchivedCompanyDocuments(qs)
        : await fetchAdminCompanyDocuments(qs);
      return {
        rows: res.data ?? [],
        totalCount: res.metadata?.pagination?.total ?? 0,
      };
    },
  });
}

export function useAdminDocuments(input: DocumentsQueryInput) {
  const query = useQuery(adminDocumentsQueryOptions(input));
  return {
    ...query,
    rows: query.data?.rows ?? [],
    totalCount: query.data?.totalCount ?? 0,
  };
}
